import React, { memo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UserProfile, useRestApiProvider } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import {
  Button,
  ConfirmationPopup,
  LoadingAnimation,
  TextField,
} from "@jugl-web/ui-components/cross-platform";
import { useProfileUpdateToast } from "../../hooks/useProfileUpdateToast";

type Inputs = {
  country: string;
  state: string;
  city: string;
};

const schema = yup.object().shape({
  country: yup.string().required(),
  state: yup.string().required(),
  city: yup.string().required(),
});

const EditBirthPlaceFormContainer = memo(() => {
  const { usersApi } = useRestApiProvider();
  const { data } = usersApi.useGetUserProfileQuery({});
  return <EditBirthPlaceFormComponent {...{ data }} />;
});

const EditBirthPlaceFormComponent: React.FC<{
  data: UserProfile | undefined;
}> = ({ data }) => (
  <div className="w-1/2">
    {!data ? (
      <LoadingAnimation />
    ) : (
      <EditBirthPlaceForm data={data} type={!data.native ? "add" : "update"} />
    )}
  </div>
);

const EditBirthPlaceForm: React.FC<{
  data: UserProfile;
  type: "add" | "update";
}> = ({ data, type }) => {
  const {
    register,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<Inputs>({
    resolver: yupResolver(schema),
    values: {
      country: data.native?.country || "",
      state: data.native?.state || "",
      city: data.native?.city || "",
    },
  });
  const [openModal, setOpenModal] = useState(false);
  const { usersApi } = useRestApiProvider();
  const [addBirth, { isLoading }] = usersApi.useAddUserProfileSectionMutation();
  const [updateBirth, { isLoading: isLoadingUpdate }] =
    usersApi.useUpdateUserProfileSectionMutation();
  const [deleteBirth, { isLoading: isLoadingDelete }] =
    usersApi.useDeleteUserProfileSectionMutation();
  const { showProfileUpdatedSnackbar } = useProfileUpdateToast();
  const { t } = useTranslations();

  const onSubmit: SubmitHandler<Inputs> = async (params) => {
    const { city, state, country } = { ...params };
    let res;
    if (type === "add") {
      res = await addBirth({
        data: {
          type: "native",
          city,
          state,
          country_name: country,
        },
      });
    } else {
      res = await updateBirth({
        item_id: data.native?.id,
        data: {
          type: "native",
          city,
          state,
          country_name: country,
        },
      });
    }
    if ("data" in res) {
      showProfileUpdatedSnackbar();
    }
  };

  const onDeleteBirth = async () => {
    const res = await deleteBirth({
      type: "native",
      item_id: data.native?.id,
    });
    if ("data" in res) {
      showProfileUpdatedSnackbar();
      setOpenModal(false);
    }
  };

  return (
    <>
      <div className="pt-10">
        <span className="pl-5 font-medium">
          {t({
            id: "birth-place-form-page.birth-place",
            defaultMessage: "Add Native / Birth Place",
          })}
        </span>
      </div>
      <div className="pt-5 pl-10">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex w-full gap-[70px]">
            <div className="flex w-1/2 flex-col gap-12">
              <TextField
                isRequired
                label={t({
                  id: "form-controls.country.label",
                  defaultMessage: "Country",
                })}
                isFullWidth
                placeholder={t({
                  id: "form-controls.country-name.placeholder",
                  defaultMessage: "Country Name",
                })}
                {...register("country")}
              />
              <TextField
                isRequired
                label={t({
                  id: "form-controls.city.label",
                  defaultMessage: "City",
                })}
                isFullWidth
                placeholder={t({
                  id: "form-controls.city-name.placeholder",
                  defaultMessage: "City Name",
                })}
                {...register("city")}
              />
              <Button
                isDisabled={
                  !isValid || isLoading || isLoadingUpdate || !isDirty
                }
                variant="contained"
                type="submit"
                fullWidth
              >
                {t({
                  id: "common.save",
                  defaultMessage: "Save",
                })}
              </Button>
            </div>
            <div className="flex w-1/2 flex-col gap-12">
              <TextField
                isRequired
                label={t({
                  id: "form-controls.state.label",
                  defaultMessage: "State",
                })}
                isFullWidth
                placeholder={t({
                  id: "form-controls.state-name.placeholder",
                  defaultMessage: "State Name",
                })}
                {...register("state")}
              />
              {data.native !== null && (
                <Button
                  isDisabled={isLoadingDelete}
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenModal(true)}
                  className="mt-auto"
                >
                  {t({
                    id: "birth-place-form-page.delete-current-city-info",
                    defaultMessage: "Delete Current City Info",
                  })}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
      {openModal && (
        <ConfirmationPopup
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
          hasCancelButton
          message={t({
            id: "birth-place-form-page.city-information-removal-confirmation",
            defaultMessage:
              "Do you really want to remove current city information?",
          })}
          variant="web"
          title={t({
            id: "delete-modal.delete-confirmation-title",
            defaultMessage: "Delete Confirmation",
          })}
          buttons={[
            {
              label: t({
                id: "common.delete",
                defaultMessage: "Delete",
              }),
              color: "tertiary",
              onClick: onDeleteBirth,
              isDisabled: isLoading,
            },
          ]}
        />
      )}
    </>
  );
};

export default EditBirthPlaceFormContainer;
